import { useMemo } from 'react';
import { toast } from 'sonner';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";

/**
 * 术语项接口
 * @interface Term
 * @property {string} source - 源语言术语
 * @property {string} target - 目标语言术语
 * @property {string} [context] - 术语上下文/说明
 */
interface Term {
  source: string;
  target: string;
  context?: string;
}

/**
 * 术语高亮组件属性
 * @interface TermHighlighterProps
 * @property {string} text - 要显示的文本
 * @property {Term[]} terms - 术语列表
 * @property {string} [className] - 额外的 CSS 类名
 * @property {'source' | 'target'} [matchField='source'] - 用哪个字段匹配文本：
 *   原文用 'source'（默认），译文用 'target'
 * @property {(target: string) => void} [onInsertTarget] - 插入译文术语到编辑器的回调；
 *   仅原文区（matchField='source'）传入时显示"插入到译文"按钮，替代原复制按钮
 */
interface TermHighlighterProps {
  text: string;
  terms: Term[];
  className?: string;
  matchField?: 'source' | 'target';
  onInsertTarget?: (target: string) => void;
}

/**
 * 转义正则表达式特殊字符
 * 
 * @param {string} string - 要转义的字符串
 * @returns {string} 转义后的字符串
 */
function escapeRegExp(string: string): string {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * 术语高亮组件
 * 
 * 功能：
 * 1. 自动识别并高亮文本中的术语
 * 2. 支持固定搭配（多词术语）的识别
 * 3. 鼠标悬停显示术语译文
 * 4. 避免重复高亮（优先匹配最长的术语）
 * 
 * @param {TermHighlighterProps} props - 组件属性
 * @returns {JSX.Element} 高亮后的文本
 */
export function TermHighlighter({ text, terms, className = '', matchField = 'source', onInsertTarget }: TermHighlighterProps) {
  /**
   * 解析文本并生成高亮片段
   * 使用贪心算法优先匹配最长的术语
   * matchField='source' 时用 term.source 匹配（原文区域），
   * matchField='target' 时用 term.target 匹配（译文区域）
   */
  const segments = useMemo(() => {
    if (!terms || terms.length === 0) {
      return [{ type: 'text' as const, content: text }];
    }

    // 按匹配字段长度降序排序，优先匹配长术语
    const sortedTerms = [...terms].sort((a, b) => b[matchField].length - a[matchField].length);

    const result: Array<{ type: 'text' | 'term'; content: string; term?: Term }> = [];
    let remainingText = text;

    while (remainingText.length > 0) {
      let matched = false;

      // 尝试匹配每个术语
      for (const term of sortedTerms) {
        const escapedSource = escapeRegExp(term[matchField]);
        // 使用单词边界匹配，避免部分匹配
        const regex = new RegExp(`^(${escapedSource})(?![\\w-])`, 'i');
        const match = remainingText.match(regex);

        if (match) {
          const matchedText = match[1];
          result.push({
            type: 'term',
            content: matchedText,
            term
          });
          remainingText = remainingText.slice(matchedText.length);
          matched = true;
          break;
        }
      }

      if (!matched) {
        // 没有匹配到术语，取第一个字符作为普通文本
        // 但为了性能，我们尝试找到下一个可能的匹配位置
        let nextMatchIndex = remainingText.length;

        for (const term of sortedTerms) {
          const escapedSource = escapeRegExp(term[matchField]);
          const regex = new RegExp(`(${escapedSource})(?![\\w-])`, 'i');
          const match = remainingText.slice(1).match(regex);

          if (match && match.index !== undefined) {
            nextMatchIndex = Math.min(nextMatchIndex, match.index + 1);
          }
        }

        result.push({
          type: 'text',
          content: remainingText.slice(0, nextMatchIndex)
        });
        remainingText = remainingText.slice(nextMatchIndex);
      }
    }

    return result;
  }, [text, terms, matchField]);

  // 如果没有术语需要高亮，直接返回原文本
  if (!terms || terms.length === 0) {
    return <span className={className}>{text}</span>;
  }

  return (
    <span className={className}>
      {segments.map((segment, index) => {
        if (segment.type === 'term' && segment.term) {
          return (
            <Popover key={index}>
              <PopoverTrigger asChild>
                <mark
                  className={`
                    cursor-pointer rounded px-0.5 font-medium
                    bg-info/15 text-black underline decoration-info/50 decoration-dotted underline-offset-4
                    hover:bg-info/25 transition-colors
                  `}
                >
                  {segment.content}
                </mark>
              </PopoverTrigger>
              <PopoverContent
                side="top"
                className="max-w-xs w-64"
                sideOffset={5}
              >
                <div className="space-y-1.5">
                  <div className="flex items-center gap-2">
                    {matchField === 'source' ? (
                      <>
                        <span className="font-semibold text-foreground">
                          {segment.term.source}
                        </span>
                        <span className="text-muted-foreground">→</span>
                        <span className="font-semibold text-primary">
                          {segment.term.target}
                        </span>
                      </>
                    ) : (
                      <>
                        <span className="font-semibold text-primary">
                          {segment.term.target}
                        </span>
                        <span className="text-muted-foreground">←</span>
                        <span className="font-semibold text-foreground">
                          {segment.term.source}
                        </span>
                      </>
                    )}
                  </div>
                  {segment.term.context && (
                    <p className="text-[11px] text-muted-foreground border-t pt-1">
                      {segment.term.context}
                    </p>
                  )}
                  {/* 操作按钮区：
                      - 原文区（matchField='source'）且传入了 onInsertTarget：显示"插入到译文"按钮，
                        把 term.target 插入到该段译文编辑器光标位置
                      - 其他场景（译文区显示态）：保留"复制译文"按钮，供选中复制 */}
                  <div className="flex justify-end gap-1.5 border-t pt-1.5">
                    {matchField === 'source' && onInsertTarget ? (
                      <button
                        type="button"
                        onClick={() => onInsertTarget(segment.term!.target)}
                        className="text-[11px] px-1.5 py-0.5 rounded border border-primary/40 text-primary hover:bg-primary/10 transition-colors"
                      >
                        插入到译文
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => {
                          navigator.clipboard.writeText(segment.term!.target)
                            .then(() => toast.success('已复制译文术语'))
                            .catch(() => toast.error('复制失败'));
                        }}
                        className="text-[11px] px-1.5 py-0.5 rounded border border-border hover:bg-accent transition-colors"
                      >
                        复制译文
                      </button>
                    )}
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          );
        }
        
        // 对普通文本片段中的格式标签进行渲染（DOCX {1} 与 ASS {\an8} 均支持）
        const parts = segment.content.split(/(\{\/?\d+\}|\{\\[^}]+\})/g)
        if (parts.length <= 1) {
          return <span key={index}>{segment.content}</span>
        }
        return (
          <span key={index}>
            {parts.map((part, i) => {
              const match = part.match(/^\{(\/?\d+|\\[^}]+)\}$/)
              if (match) {
                const label = match[1]
                return (
                  <Badge
                    key={i}
                    variant="outline"
                    className="text-[10px] px-1 h-4 min-w-[18px] justify-center font-mono bg-warning/10 text-warning border-warning/30 mx-0.5 select-none inline-flex"
                  >
                    {label}
                  </Badge>
                )
              }
              return <span key={i}>{part}</span>
            })}
          </span>
        )
      })}
    </span>
  );
}

/**
 * 100%匹配高亮组件
 * 
 * 用于高亮与历史翻译完全匹配的句子
 * 
 * @param {Object} props - 组件属性
 * @param {string} props.text - 要显示的文本
 * @param {boolean} props.isMatch - 是否为100%匹配
 * @param {string} [props.matchedTranslation] - 匹配的历史译文
 * @param {string} [props.className] - 额外的 CSS 类名
 * @returns {JSX.Element} 高亮后的文本
 */
export function ExactMatchHighlighter({ 
  text, 
  isMatch, 
  matchedTranslation,
  className = '' 
}: { 
  text: string; 
  isMatch: boolean; 
  matchedTranslation?: string;
  className?: string;
}) {
  if (!isMatch) {
    return <span className={className}>{text}</span>;
  }

  return (
    <TooltipProvider>
      <Tooltip delayDuration={200}>
        <TooltipTrigger asChild>
          <mark
            className="cursor-help rounded px-0.5 bg-success/10 text-success-foreground font-medium"
          >
            {text}
          </mark>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-sm" sideOffset={5}>
          <div className="space-y-1.5">
            <div className="flex items-center gap-1.5">
              <span className="inline-block w-2 h-2 rounded-full bg-success" />
              <span className="text-[10px] font-medium text-success-foreground">100% 匹配</span>
            </div>
            {matchedTranslation && (
              <div className="border-t pt-1.5">
                <p className="text-[10px] text-muted-foreground mb-1">历史译文：</p>
                <p className="text-xs text-foreground">{matchedTranslation}</p>
              </div>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export default TermHighlighter;
